import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";
import { EditUserDialog } from "./EditUserDialog";
import { Button } from "@/components/ui/button";
import { Pencil } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];
type UserRole = Database["public"]["Tables"]["user_roles"]["Row"];
type AppRole = Database["public"]["Enums"]["app_role"];

type UserWithRole = Profile & {
  roles: UserRole[];
};

interface UserTableProps {
  users: UserWithRole[];
  onRoleUpdate: () => void;
}

export const UserTable = ({ users, onRoleUpdate }: UserTableProps) => {
  const { toast } = useToast();
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const updateUserRole = async (userId: string, newRole: AppRole) => {
    try {
      const { error: deleteError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId);

      if (deleteError) throw deleteError;

      const { error: insertError } = await supabase
        .from('user_roles')
        .insert({
          user_id: userId,
          role: newRole
        });

      if (insertError) throw insertError;

      toast({
        title: "Rolle opdateret",
        description: "Brugerens rolle er blevet opdateret",
      });

      onRoleUpdate();
    } catch (error) {
      toast({
        title: "Fejl ved opdatering af rolle",
        description: "Der skete en fejl. Prøv igen senere.",
        variant: "destructive",
      });
    }
  };

  const handleEditClick = (user: UserWithRole) => {
    setSelectedUser(user);
    setIsEditDialogOpen(true);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="font-semibold text-gray-900">Navn</TableHead>
            <TableHead className="font-semibold text-gray-900">Email</TableHead>
            <TableHead className="font-semibold text-gray-900">Telefon</TableHead>
            <TableHead className="font-semibold text-gray-900">Firma</TableHead>
            <TableHead className="font-semibold text-gray-900">Rolle</TableHead>
            <TableHead className="font-semibold text-gray-900">Handlinger</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell className="font-medium text-gray-900">
                {user.first_name} {user.last_name}
              </TableCell>
              <TableCell className="text-gray-900">{user.email}</TableCell>
              <TableCell className="text-gray-900">{user.phone}</TableCell>
              <TableCell className="text-gray-900">{user.company}</TableCell>
              <TableCell className="text-gray-900">
                {user.roles[0]?.role || 'Ingen rolle'}
              </TableCell>
              <TableCell className="space-x-2">
                <Select
                  onValueChange={(value) => updateUserRole(user.id, value as AppRole)}
                  defaultValue={user.roles[0]?.role}
                >
                  <SelectTrigger className="w-32 bg-white border-gray-200 text-gray-900">
                    <SelectValue placeholder="Vælg rolle" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200 shadow-lg">
                    <SelectItem value="admin" className="text-gray-900 hover:bg-gray-100">Admin</SelectItem>
                    <SelectItem value="employee" className="text-gray-900 hover:bg-gray-100">Medarbejder</SelectItem>
                    <SelectItem value="customer" className="text-gray-900 hover:bg-gray-100">Kunde</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleEditClick(user)}
                  className="ml-2"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {selectedUser && (
        <EditUserDialog
          isOpen={isEditDialogOpen}
          onClose={() => {
            setIsEditDialogOpen(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          onUserUpdated={onRoleUpdate}
        />
      )}
    </div>
  );
};